import { withDependencies, named, optional } from '@wix/thunderbolt-ioc'
import { ISessionManager, SessionManagerSymbol } from 'feature-session-manager'
import { uniqueId, noop } from 'lodash'
import { BsiManagerSymbol, BusinessLogger, IBsiManager } from 'feature-business-logger'
import {
	SiteFeatureConfigSymbol,
	Fetch,
	IFetchApi,
	ILogger,
	LoggerSymbol,
	ViewerModel,
	ViewerModelSym,
	IPropsStore,
	IStructureAPI,
	ILanguage,
	BrowserWindow,
	Props,
	StructureAPI,
	LanguageSymbol,
	BrowserWindowSymbol,
	ExperimentsSymbol,
	Experiments,
	ICyclicTabbing,
	FeatureExportsSymbol,
	BusinessLoggerSymbol,
	MasterPageFeatureConfigSymbol,
} from '@wix/thunderbolt-symbols'
import { CyclicTabbingSymbol } from 'feature-cyclic-tabbing'
import type {
	MemberDetails,
	ISiteMembersApi,
	SocialAuthComponentProps,
	SiteMembersSiteConfig,
	SiteMembersMasterPageConfig,
} from '../types'
import { name } from '../symbols'
import { getPerformFetch, memberDetailsFromDTO, _getSocialAuthComponentProps } from '../utils'
import { CommonProps, getDialogService } from '../dialogService'
import { ISiteScrollBlocker, SiteScrollBlockerSymbol } from 'feature-site-scroll-blocker'
import { IFeatureExportsStore } from 'thunderbolt-feature-exports'
import { INTERACTIONS } from '../constants'

const siteMembersFactory = (
	// NOTE: In the editor the models are being updated by ref it means the config experience mutations
	siteMembersConfig: SiteMembersSiteConfig,
	siteMembersMasterPageConfig: SiteMembersMasterPageConfig,
	// NOTE: In the editor the models are being updated by ref it means the config experience mutations
	viewerModel: ViewerModel,
	fetchApi: IFetchApi,
	logger: ILogger,
	sessionManager: ISessionManager,
	propsStore: IPropsStore,
	structureApi: IStructureAPI,
	language: ILanguage,
	browserWindow: BrowserWindow,
	siteScrollBlocker: ISiteScrollBlocker,
	experiments: Experiments,
	cyclicTabbing: ICyclicTabbing,
	siteMembersExports: IFeatureExportsStore<typeof name>,
	businessLogger: BusinessLogger,
	bsiManager?: IBsiManager
): ISiteMembersApi => {
	let memberDetails = {} as MemberDetails
	const { translations } = siteMembersMasterPageConfig
	const performFetch = getPerformFetch(
		fetchApi,
		{
			credentials: 'same-origin',
			headers: {
				accept: 'application/json',
				'x-wix-site-revision': `${viewerModel.site.siteRevision}`,
				'x-wix-client-artifact-id': 'thunderbolt',
			},
		},
		viewerModel.requestUrl
	)
	const onMemberDetailsRefresh: { [callbackId: string]: () => void } = {}
	const dialogService = getDialogService(propsStore, structureApi, siteScrollBlocker, browserWindow, cyclicTabbing)

	const dialogOptions = {
		noop,
		shouldWaitForAppDidMount: false,
	}
	const triggerOnMemberDetailsRefreshCallbacks = () => {
		return Promise.all(
			Object.values(onMemberDetailsRefresh).map(async (cb) => {
				try {
					await cb()
				} catch (e) {
					logger.captureError(e, { tags: { feature: 'site-members' } })
				}
			})
		)
	}
	const authDialogAction = {
		onCloseDialogCallback: () => {
			dialogService.hideDialog()
		},
		submit: async () => {},
		onForgetYourPasswordClick: noop,
		onSwitchDialogLinkClick: noop,
		// onTokenMessage about to be deprecated
		onTokenMessage: async () => {},
		onBackendSocialLogin: async () => {},
	}
	const api: ISiteMembersApi = {
		async getMemberDetails(refreshCurrentMember: boolean = false): Promise<MemberDetails | null> {
			if (memberDetails.id && !refreshCurrentMember) {
				return memberDetails
			}

			try {
				const getMemberDetailsUrl = `/_api/wix-sm-webapp/member/?collectionId=${siteMembersConfig.smcollectionId}&metaSiteId=${viewerModel.site.metaSiteId}`
				const { payload } = await performFetch(getMemberDetailsUrl)

				memberDetails = memberDetailsFromDTO(payload)
				siteMembersExports.export({ memberDetails })

				if (refreshCurrentMember) {
					await triggerOnMemberDetailsRefreshCallbacks()
				}

				return memberDetails
			} catch (error) {
				logger.captureError(error, { tags: { feature: 'site-members' } })
				throw error
			}
		},
		registerToMemberDetailsRefresh(callback: () => any): string {
			const callbackId = uniqueId('mdrcb')
			onMemberDetailsRefresh[callbackId] = callback
			return callbackId
		},
		unRegisterToMemberDetailsRefresh(callbackId: string): void {
			delete onMemberDetailsRefresh[callbackId]
		},
		getSocialAuthComponentProps(): SocialAuthComponentProps {
			const reportSocialAuthStarted = (vendor: string) => {
				logger.interactionStarted(INTERACTIONS.SOCIAL_APP_LOGIN_WITH_VENDOR(vendor))
			}
			return _getSocialAuthComponentProps({
				config: siteMembersConfig,
				viewerModel,
				sessionManager,
				bsiManager,
				isSocialAuthSupported: true,
				userLanguage: language.userLanguage,
				reportBi: businessLogger.reportBi,
				reportSocialAuthStarted,
				useNewSocialFlow: false,
			})
		},
		async showLoginDialog(
			isCloseable: boolean = true,
			displayMode: CommonProps['displayMode'] = 'fullscreen'
		): Promise<void> {
			const props = {
				bsi: '00000000-0000-0000-0000-000000000000',
				displayMode,
				language: language.userLanguage,
				directionByLanguage: language.directionByLanguage,
				isCloseable,
				smCollectionId: '',
				svSession: '',
				biVisitorId: '',
				metaSiteId: viewerModel.site.metaSiteId,
				isSocialLoginGoogleEnabled: true,
				isSocialLoginFacebookEnabled: true,
				isEmailLoginEnabled: true,
				isSocialAuthSupported: true,
				reportBi: businessLogger.reportBi,
				useNewSocialFlow: false,
				translations,
			}

			return dialogService.displayDialog('MemberLoginDialog', props, authDialogAction, dialogOptions)
		},
		async showSignUpDialog(
			isCloseable: boolean = true,
			displayMode: CommonProps['displayMode'] = 'fullscreen'
		): Promise<void> {
			const props = {
				bsi: '00000000-0000-0000-0000-000000000000',
				displayMode,
				language: language.userLanguage,
				directionByLanguage: language.directionByLanguage,
				isCloseable,
				smCollectionId: '',
				svSession: '',
				biVisitorId: '',
				metaSiteId: viewerModel.site.metaSiteId,
				isSocialLoginGoogleEnabled: true,
				isSocialLoginFacebookEnabled: true,
				isSocialAuthSupported: true,
				isEmailLoginEnabled: true,
				isCommunityInstalled: false,
				privacyNoteType: 'CHECKBOX' as const,
				joinCommunityCheckedByDefault: false,
				isTermsOfUseNeeded: false,
				isPrivacyPolicyNeeded: false,
				isCodeOfConductNeeded: false,
				reportBi: businessLogger.reportBi,
				useNewSocialFlow: false,

				termsOfUseLink: undefined,
				privacyPolicyLink: undefined,
				codeOfConductLink: undefined,
				translations,
			}

			return dialogService.displayDialog('SignUpDialog', props, authDialogAction, dialogOptions)
		},
		async hideAuthDialog(): Promise<void> {
			return dialogService.hideDialog()
		},
		// @ts-ignore
		login: noop,
		// @ts-ignore
		promptLogin: noop,
		// @ts-ignore
		promptForgotPassword: noop,
		// @ts-ignore
		requestAuthentication: noop,
		// @ts-ignore
		requestAuthorizedPages: noop,
		// @ts-ignore
		applySessionToken: noop,
		// @ts-ignore
		authorizeMemberPagesByToken: noop,
		// @ts-ignore
		authorizeMemberPagesByCookie: noop,
		// @ts-ignore
		authorizeMemberPagesBySignedInstance: noop,
		// @ts-ignore
		register: noop,
		// @ts-ignore
		sendForgotPasswordMail: noop,
		// @ts-ignore
		changePassword: noop,
		// @ts-ignore
		registerToUserLogin: noop,
		// @ts-ignore
		unRegisterToUserLogin: noop,
		// @ts-ignore
		registerToMemberLogout: noop,
		// @ts-ignore
		unRegisterToMemberLogout: noop,
		// @ts-ignore
		logout: noop,
		// @ts-ignore
		handleOauthToken: noop,
		// @ts-ignore
		handleSocialLoginResponse: noop,
		// @ts-ignore
		showNoPermissionsToPageDialog: noop,
		// @ts-ignore
		shouldDialogsBlockRendering: noop,
		// @ts-ignore
		showRenderingBlockingDialogs: noop,
		// @ts-ignore
		showResetPasswordDialog: noop,
		// @ts-ignore
		showWelcomeDialog: noop,
		async appWillMount() {
			if (siteMembersConfig.loginSocialBarOnSite && siteMembersConfig.smcollectionId) {
				await api.getMemberDetails()
			}
		},
	}

	siteMembersExports.export({
		promptLogin: api.promptLogin,
		logout: api.logout,
		memberDetails,
	})
	return api
}
export const SiteMembers = withDependencies(
	[
		named(SiteFeatureConfigSymbol, name),
		named(MasterPageFeatureConfigSymbol, name),
		ViewerModelSym,
		Fetch,
		LoggerSymbol,
		SessionManagerSymbol,
		Props,
		StructureAPI,
		LanguageSymbol,
		BrowserWindowSymbol,
		SiteScrollBlockerSymbol,
		ExperimentsSymbol,
		CyclicTabbingSymbol,
		named(FeatureExportsSymbol, name),
		BusinessLoggerSymbol,
		optional(BsiManagerSymbol),
	],
	siteMembersFactory
)
